import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  FileText,
  Lightbulb,
  ListTodo,
  Send,
  Loader2,
  Copy,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = {
  summary: [
    "Summarize our program proposals for this quarter",
    "Create a summary of recent library events",
    "Summarize the status of our yearly goals",
    "Generate a book collection overview"
  ],
  recommendation: [
    "Suggest new programs for children ages 5-10",
    "Recommend improvements for our literacy stations",
    "What events should we plan for the summer?",
    "Suggest ways to increase community engagement"
  ],
  todo: [
    "Create a weekly to-do list for library staff",
    "What tasks should we prioritize this month?",
    "Generate a checklist for setting up a book display",
    "List tasks needed to launch a new literacy program"
  ]
};

export const AIAssistant = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    setLoading(true);
    setResponse("");

    try {
      const result = await axios.post(`${API}/ai/generate`, {
        prompt: prompt,
        context: context,
        request_type: activeTab
      });
      setResponse(result.data.response);
      toast.success("Generated successfully!");
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate response");
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (preset) => {
    setPrompt(preset);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case "summary":
        return <FileText className="h-4 w-4" />;
      case "recommendation":
        return <Lightbulb className="h-4 w-4" />;
      case "todo":
        return <ListTodo className="h-4 w-4" />;
      default:
        return <Sparkles className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-amber-100">
            <Sparkles className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            AI Assistant
          </h1>
        </div>
        <p className="text-muted-foreground">
          Generate summaries, recommendations, and to-do lists powered by AI
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-6">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-heading text-xl">What would you like?</CardTitle>
              <CardDescription>
                Choose a type and describe what you need
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 w-full" data-testid="ai-type-tabs">
                  <TabsTrigger value="summary" data-testid="tab-summary" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Summary
                  </TabsTrigger>
                  <TabsTrigger value="recommendation" data-testid="tab-recommendation" className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    Ideas
                  </TabsTrigger>
                  <TabsTrigger value="todo" data-testid="tab-todo" className="flex items-center gap-2">
                    <ListTodo className="h-4 w-4" />
                    To-Do
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="mt-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate concise summaries of your library data, reports, and activities.
                  </p>
                </TabsContent>
                <TabsContent value="recommendation" className="mt-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Get AI-powered recommendations for programs, events, and improvements.
                  </p>
                </TabsContent>
                <TabsContent value="todo" className="mt-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Create prioritized to-do lists and action items for your team.
                  </p>
                </TabsContent>
              </Tabs>

              <div>
                <Label htmlFor="prompt">Your Request *</Label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe what you'd like to generate..."
                  rows={4}
                  className="mt-1"
                  data-testid="ai-prompt-input"
                />
              </div>

              <div>
                <Label htmlFor="context">Additional Context (Optional)</Label>
                <Textarea
                  id="context"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Add any relevant information, data, or constraints..."
                  rows={3}
                  className="mt-1"
                  data-testid="ai-context-input"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={loading || !prompt.trim()}
                className="w-full btn-animate"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Generate
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Quick Presets */}
          <Card className="border-stone-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">Quick Prompts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {PRESETS[activeTab].map((preset, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => handlePresetClick(preset)}
                    data-testid={`preset-${index}`}
                  >
                    {preset}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Output Section */}
        <Card className="border-stone-200 h-fit" data-testid="ai-response-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getTabIcon(activeTab)}
                <CardTitle className="font-heading text-xl capitalize">
                  {activeTab === "todo" ? "To-Do List" : activeTab}
                </CardTitle>
              </div>
              {response && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  data-testid="copy-response-btn"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600 mb-4" />
                <p className="text-sm text-muted-foreground">Generating your {activeTab}...</p>
              </div>
            ) : response ? (
              <div 
                className="prose prose-sm max-w-none ai-highlight rounded-lg p-4"
                data-testid="ai-response-content"
              >
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {response}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="p-4 rounded-full bg-stone-100 mb-4">
                  <Sparkles className="h-8 w-8 text-stone-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-medium text-lg mb-1">Ready to assist</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Enter your request and click Generate to get AI-powered {activeTab === "todo" ? "to-do lists" : `${activeTab}s`}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tips Section */}
      <Card className="border-amber-200 bg-gradient-to-r from-stone-50 to-amber-50/30">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg font-semibold mb-3">Tips for Better Results</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium mb-1">Be Specific</p>
              <p className="text-muted-foreground">
                Include details like time periods, target audiences, or specific programs.
              </p>
            </div>
            <div>
              <p className="font-medium mb-1">Add Context</p>
              <p className="text-muted-foreground">
                Provide relevant data, constraints, or background information.
              </p>
            </div>
            <div>
              <p className="font-medium mb-1">Iterate</p>
              <p className="text-muted-foreground">
                Refine your prompts based on the responses you receive.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
